import Highcharts from 'highcharts';
import tinyColor from 'tinycolor2';

import { ChartColors } from 'theme/tokens';

// Chart colour options. Colours are chart tokens (--color-chart-N), kept as
// token ids and resolved to their hex values here: Highcharts can't lighten,
// blend or fade a CSS variable. The tokens are the same in both themes.
// Accounts can also make their own palettes (in Studio), of hex values,
// which charts refer to by ChartPalettes.Ref.

type PaletteId = ChartPaletteId | ChartPalettes.Ref;

export interface ChartPalette {
  id: ChartPaletteId;
  label: string;
  // Absent for the default palette, which is every chart colour in order
  colorIds?: ChartColorId[];
}

const ids = (...ns: number[]): ChartColorId[] =>
  ns.map((n): ChartColorId => `chart-${n}`);

// Each palette's own tokens, then (see getChartPalette) the rest of the
// chart colours, so many series stay distinct. Palettes saved with an id no
// longer offered draw with the default colours.
export const CHART_PALETTES: ChartPalette[] = [
  { id: 'default', label: 'Default' },
  { id: 'macaron', label: 'Macaron', colorIds: ids(38, 39, 40, 41, 42) },
  { id: 'pastel', label: 'Pastel', colorIds: ids(43, 44, 45, 46, 47) },
  { id: 'dusk', label: 'Dusk', colorIds: ids(48, 49, 50, 51, 52) },
  { id: 'meadow', label: 'Meadow', colorIds: ids(53, 54, 55, 56, 57) },
  {
    id: 'vivid',
    label: 'Vivid',
    colorIds: ids(10, 11, 22, 36, 28, 8, 3, 21),
  },
];

// Offered for a metric's own colour, beside a picker for any other
export const SERIES_COLOR_IDS: ChartColorId[] = ids(
  1,
  3,
  19,
  28,
  23,
  22,
  5,
  21,
  7,
  4,
  2,
  6,
  36,
  18,
  11,
  17,
  8,
  29,
);

// A metric's own colour: a chart token, or one picked freely
type SeriesColor = ChartColorId | ChartPalettes.Hex;

export const isHexColor = (color: SeriesColor): color is ChartPalettes.Hex =>
  color.startsWith('#');

const DEFAULT_COLOR_IDS = ChartColors.map(
  (_color, index): ChartColorId => `chart-${index + 1}`,
);

export const getPaletteColorIds = (
  paletteId: PaletteId | undefined,
): ChartColorId[] =>
  CHART_PALETTES.find((p) => p.id === paletteId)?.colorIds ?? DEFAULT_COLOR_IDS;

export const toAccountPaletteRef = (id: string): ChartPalettes.Ref =>
  `account:${id}`;

export const isAccountPaletteRef = (
  paletteId: PaletteId | undefined,
): paletteId is ChartPalettes.Ref =>
  !!paletteId && paletteId.startsWith('account:');

// The account palette a chart refers to, if it (still) exists and has colours
const findAccountPalette = ({
  paletteId,
  accountPalettes,
}: {
  paletteId: PaletteId | undefined;
  accountPalettes: ChartPalettes.AccountPalette[];
}): ChartPalettes.AccountPalette | undefined =>
  isAccountPaletteRef(paletteId)
    ? accountPalettes.find(
        (p) => toAccountPaletteRef(p.id) === paletteId && p.colors.length > 0,
      )
    : undefined;

// The hex value of a chart token (the first colour for an unknown one); a
// picked hex as it is
export const toChartColor = (id: SeriesColor): string => {
  if (isHexColor(id)) {
    return id;
  }
  const n = Number(id.replace('chart-', ''));
  return ChartColors[n - 1] ?? ChartColors[0];
};

// The CSS for a chart token, for swatches outside Highcharts; a picked hex
// as it is
export const toChartColorCss = (id: SeriesColor): string =>
  isHexColor(id) ? id : `var(--color-${id})`;

// A palette's own colours as CSS, for swatches: tokens for ours, hex for an
// account's. The default colours for a palette that no longer exists.
export const getPaletteSwatchColors = ({
  paletteId,
  accountPalettes,
}: {
  paletteId: PaletteId | undefined;
  accountPalettes: ChartPalettes.AccountPalette[];
}): string[] => {
  const accountPalette = findAccountPalette({ paletteId, accountPalettes });
  return accountPalette
    ? accountPalette.colors
    : getPaletteColorIds(paletteId).map(toChartColorCss);
};

// A palette's colours, as hex, then the rest of the chart colours so many
// series or terms stay distinct
const getPaletteColors = ({
  paletteId,
  accountPalettes,
}: {
  paletteId: PaletteId | undefined;
  accountPalettes: ChartPalettes.AccountPalette[];
}): string[] => {
  const accountPalette = findAccountPalette({ paletteId, accountPalettes });
  if (accountPalette) {
    const used = accountPalette.colors.map((c) => c.toLowerCase());
    return [
      ...accountPalette.colors,
      ...ChartColors.filter((c) => !used.includes(c.toLowerCase())),
    ];
  }
  const paletteColorIds = getPaletteColorIds(paletteId);
  return [
    ...paletteColorIds,
    ...DEFAULT_COLOR_IDS.filter((id) => !paletteColorIds.includes(id)),
  ].map(toChartColor);
};

const COLOURED_BY_METRIC: GadgetType[] = [
  'timeSeries',
  'bar',
  'horizontalBar',
  'combo',
  'radar',
  'scatter',
  'bubble',
];

// Whether the chart has a series (so a colour) per metric. Pies, stacks,
// heatmaps and charts split by a second dimension colour by term instead.
export const isColouredByMetric = (
  chartDef: Pick<V5ChartDefinition, 'gadgetType' | 'dimensionB'>,
): boolean =>
  COLOURED_BY_METRIC.includes(chartDef.gadgetType) && !chartDef.dimensionB;

// The colours a chart draws with, when it customises them: its palette,
// then the rest of the chart colours so many series or terms stay distinct.
// A chart coloured by metric uses each metric's own colour at its series'
// position (series take their colour by position). Undefined when nothing
// is customised (an account palette that no longer exists customises
// nothing).
export const getChartPalette = ({
  chartDefinition,
  accountPalettes,
}: {
  chartDefinition: V5ChartDefinition;
  accountPalettes: ChartPalettes.AccountPalette[];
}): string[] | undefined => {
  const { colorPalette } = chartDefinition;
  const seriesColorIds = isColouredByMetric(chartDefinition)
    ? chartDefinition.series.map((s) => s.color)
    : [];
  const hasPalette = isAccountPaletteRef(colorPalette)
    ? !!findAccountPalette({ paletteId: colorPalette, accountPalettes })
    : !!colorPalette && colorPalette !== 'default';
  if (!hasPalette && !seriesColorIds.some((id) => !!id)) {
    return undefined;
  }
  const colors = getPaletteColors({ paletteId: colorPalette, accountPalettes });
  seriesColorIds.forEach((id, index) => {
    if (id) {
      colors[index] = toChartColor(id);
    }
  });
  return colors;
};

const HEATMAP_MIN_LIGHTEN = 35;

// The colours a heatmap's scale runs between, from none up to the most: the
// palette's first colour, lightened, up to the colour itself
export const toHeatmapScale = (
  colors: string[],
): { minColor: string; maxColor: string } => ({
  minColor: tinyColor(colors[0]).lighten(HEATMAP_MIN_LIGHTEN).toHexString(),
  maxColor: colors[0],
});

// Final Highcharts options drawn with the palette: its colours, and (for a
// heatmap) its colour axis, scaled from the first colour as by default
export const withChartPalette = ({
  options,
  colors,
}: {
  options: Highcharts.Options;
  colors: string[];
}): Highcharts.Options => {
  const { colorAxis } = options;
  return {
    ...options,
    colors,
    ...(colorAxis && !Array.isArray(colorAxis)
      ? {
          colorAxis: { ...colorAxis, ...toHeatmapScale(colors) },
        }
      : {}),
  };
};
